"use client";

//Example: https://mui.com/material-ui/react-drawer/#swipeable-edge
//API: https://mui.com/material-ui/api/swipeable-drawer/
import cn from "classnames";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";

import { StyleHooks, ProviderHooks } from "@/src/hook";
import { FrontendTrackingService } from "@/src/service";
import { BrokerProfileType, StrapiCollectionType, StrapiComponentType } from "@/src/type";
import { StringHelper } from "@/src/util";

import { Contact, Messages, ProfileBody } from "./component";

import styles from "./ProfileDrawer.module.scss";
import { KEYS } from "@/src/const";

function promoToProfile(
  profile?: BrokerProfileType.BrokerProfile | null,
  promo?: StrapiCollectionType.Promo | null
): BrokerProfileType.BrokerProfile | null | undefined {
  if (profile?.identityId) {
    return profile;
  }

  const profileBanner = promo?.promoControl?.[0] as StrapiComponentType.Object.ProfileBanner;
  if (profileBanner?.slug) {
    return {
      id: profileBanner.id,
      firstName: profileBanner.title,
      lastName: "",
      email: profileBanner.email,
      profileSlug: profileBanner.slug,
      countryCode: profileBanner.countryCode ?? "",
      phoneNumber: profileBanner.phoneNumber,
      whatsApp: profileBanner.whatsApp,
      bio: profileBanner.description,
      brokerageName: profileBanner.subtitle,
      brokerageWebsite: profileBanner.button1Link,
      brokerageLogo: profileBanner.largeLogo,
      optionalLink: "",
      optionalLinkLabel: "",
      headshot: profileBanner.smallLogo,
      identityId: `${profileBanner.slug}`,
    };
  }
}

const DRAWER_BLEEDING_UP = 86;

interface ProfileDrawerProps {
  profile?: BrokerProfileType.BrokerProfile | null;
  searchParams?: Record<string, string | string[] | undefined>;
  promo?: StrapiCollectionType.Promo | null;
  pushURL?: (url: string) => void;
}

function ProfileDrawer({ profile, promo, searchParams, pushURL }: ProfileDrawerProps) {
  const profileOpen = StringHelper.checkIsStringOrIsNull(searchParams?.["profileDrawer"], "false");
  const cms = ProviderHooks.useCMS();
  const params = useSearchParams();
  const isMobile = StyleHooks.useBreakPointDown("md");
  const zoom = params?.get("zoom");
  const [iOS, setIOS] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(profileOpen === "open");
  const [desktopHeightOpen, setDesktopHeightOpen] = useState<boolean>(profileOpen === "open");
  const [desktopProfileOpen, setDesktopProfileOpen] = useState<boolean>(true);
  const data = promoToProfile(profile, promo);
  const hasBrokerProfile = !!params?.get(KEYS.SEARCH_PARAM.BROKER_PROFILE);
  const forceOff = params?.get("profile") === "disable" || params?.get("hide") === "profile";
  const profileBanner = promo?.promoControl?.[0] as StrapiComponentType.Object.ProfileBanner;

  const push = (url: string) => {
    if (typeof pushURL === "function") {
      pushURL(url);
      return;
    }
    window.open(url, "_self");
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
    if (!data?.identityId) {
      return;
    }
    FrontendTrackingService.postTrackingLog({
      action: "click",
      actionId: data.identityId,
      actionIdType: "data.identityId",
      actionMessage: "toggleMobileDrawer",
      triggeredFrom: "ProfileDrawer.tsx",
    });
  };

  const handleProfileOpen = () => {
    setDesktopProfileOpen((desktopProfileOpen) => !desktopProfileOpen);
    if (!data?.identityId) {
      return;
    }

    FrontendTrackingService.postTrackingLog({
      action: "click",
      actionId: data.identityId,
      actionIdType: "data.identityId",
      actionMessage: "profileHorizontalMinimizeDesktop",
      triggeredFrom: "ProfileDrawer.tsx",
    });
  };

  const handleDesktopOpen = () => {
    if (!desktopProfileOpen) {
      return;
    }

    if (!hasBrokerProfile) {
      return;
    }

    setDesktopHeightOpen((desktopHeightOpen) => !desktopHeightOpen);
    if (!data?.identityId) {
      return;
    }
    FrontendTrackingService.postTrackingLog({
      action: "click",
      actionId: data.identityId,
      actionIdType: "data.identityId",
      actionMessage: "profileOpenOrCloseVerticalDesktop",
      triggeredFrom: "ProfileDrawer.tsx",
    });
  };

  function drawerHeight() {
    return open ? `calc(88% - ${DRAWER_BLEEDING_UP}px)` : `calc(50% - ${DRAWER_BLEEDING_UP}px)`;
  }

  function promoOnClick() {
    if (typeof profileBanner?.button1Link === "string") {
      FrontendTrackingService.postTrackingLog({
        action: "click",
        actionId: profileBanner.slug,
        actionIdType: "promo.slug",
        actionMessage: "promoButtonClicked",
        triggeredFrom: "ProfileDrawer.tsx",
      });
      push(cms.constructLink(profileBanner.button1Link));
    }
  }

  useEffect(() => {
    const checkIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIOS(checkIOS);
  }, []);

  if ((!hasBrokerProfile && !profileBanner) || !data?.firstName || forceOff) {
    return <></>;
  }

  if (!isMobile || profileOpen === "forceDesktop") {
    return (
      <div
        className={styles.desktop}
        style={{ display: profileOpen === "forceDesktop" ? "block !important" : undefined }}
      >
        <Messages promo={promo} />
        <div
          className={cn(styles.desktopContact, desktopHeightOpen ? styles.up : undefined, !desktopProfileOpen ? styles.close : undefined)}
          onClick={hasBrokerProfile ? undefined : promoOnClick}
        >
          <div className={styles.desktopContactBottom}>
            <Contact
              profile={data}
              isMobile={false}
              shrink={!desktopProfileOpen}
              contactLeftOnClick={handleDesktopOpen}
            />
          </div>
          <div className={cn(styles.desktopContactTop, desktopHeightOpen ? styles.up : undefined)}>
            <ProfileBody
              profile={data}
              isDesktop={true}
              promo={promo}
              hasBrokerProfile={hasBrokerProfile}
            />
            {desktopHeightOpen && <div className={styles.spacer}></div>}
          </div>
        </div>
        {!desktopHeightOpen && (
          <div
            className={cn(styles.desktopContactAction, !desktopProfileOpen ? styles.open : undefined)}
            onClick={handleProfileOpen}
          >
            <Image
              className={desktopProfileOpen ? styles.close : styles.open}
              src="/image/icon_arrow_down.svg"
              width={12}
              height={12}
              alt="Close contact sales widget"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.messages}>
        <Messages promo={promo} />
      </div>
      <SwipeableDrawer
        anchor="bottom"
        open={isMobile && open && hasBrokerProfile}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={DRAWER_BLEEDING_UP}
        disableSwipeToOpen={!hasBrokerProfile}
        sx={{
          zIndex: 1000,
          zoom: zoom,
        }}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            height: drawerHeight(),
            overflow: "visible",
            visibility: "unset !important",
          },
        }}
        SwipeAreaProps={{
          sx: {
            right: "110px",
          },
        }}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        transitionDuration={{
          appear: 500,
          enter: 500,
          exit: 50,
        }}
      >
        <Box
          className={styles.header}
          sx={{ top: `-${DRAWER_BLEEDING_UP}px` }}
        >
          {hasBrokerProfile && <div className={styles.handle}></div>}
          <Contact
            profile={data}
            isMobile={true}
            hasBrokerProfile={hasBrokerProfile}
            containerOnClick={hasBrokerProfile ? undefined : promoOnClick}
          />
        </Box>
        <ProfileBody
          profile={data}
          promo={promo}
          hasBrokerProfile={hasBrokerProfile}
        />
      </SwipeableDrawer>
    </div>
  );
}

export default ProfileDrawer;
